"use client";

import { useState, useRef, useEffect } from "react";
import { AlertCircle, ChevronLeft, ChevronRight } from "lucide-react";

const WhoIsThisFor = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const containerRef = useRef(null);

  const targetAudience = [
    {
      emoji: "🎓",
      title: "First, Second & Third year students",
      description: "Get ahead of the competition early",
      gradient: "from-blue-500/20 to-indigo-500/20",
      hoverGradient:
        "group-hover:from-blue-500/30 group-hover:to-indigo-500/30",
      borderColor: "border-blue-500/30",
      hoverBorderColor: "group-hover:border-blue-400",
      codeHint: "student.level <= 3",
    },
    {
      emoji: "🧑‍💻",
      title:
        "Who want SDE Internships at startups, big tech & everything in between",
      description: "Dream big, prepare smart",
      gradient: "from-green-500/20 to-emerald-500/20",
      hoverGradient:
        "group-hover:from-green-500/30 group-hover:to-emerald-500/30",
      borderColor: "border-green-500/30",
      hoverBorderColor: "group-hover:border-green-400",
      codeHint: "target: 'FAANG++'",
    },
    {
      emoji: "😵‍💫",
      title: "Tired of random LinkedIn advice and 1000-question DSA lists",
      description: "Cut through the noise",
      gradient: "from-purple-500/20 to-pink-500/20",
      hoverGradient:
        "group-hover:from-purple-500/30 group-hover:to-pink-500/30",
      borderColor: "border-purple-500/30",
      hoverBorderColor: "group-hover:border-purple-400",
      codeHint: "confusion = false",
    },
    {
      emoji: "😮‍💨",
      title: "Want to actually get interviews instead of just grinding",
      description: "Results over effort",
      gradient: "from-amber-500/20 to-orange-500/20",
      hoverGradient:
        "group-hover:from-amber-500/30 group-hover:to-orange-500/30",
      borderColor: "border-amber-500/30",
      hoverBorderColor: "group-hover:border-amber-400",
      codeHint: "interviews++",
    },
  ];

  const scrollToIndex = (index) => {
    if (containerRef.current) {
      const cardWidth = containerRef.current.children[0].offsetWidth; // full card width, no gap
      containerRef.current.scrollTo({
        left: index * cardWidth,
        behavior: "smooth",
      });
      setActiveIndex(index);
    }
  };

  const handleMouseDown = (e) => {
    setIsDragging(true);
    setStartX(e.pageX - containerRef.current.offsetLeft);
    setScrollLeft(containerRef.current.scrollLeft);
  };

  const handleTouchStart = (e) => {
    setIsDragging(true);
    setStartX(e.touches[0].pageX - containerRef.current.offsetLeft);
    setScrollLeft(containerRef.current.scrollLeft);
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    e.preventDefault();
    const x = e.pageX - containerRef.current.offsetLeft;
    const walk = (x - startX) * 2;
    containerRef.current.scrollLeft = scrollLeft - walk;
  };

  const handleTouchMove = (e) => {
    if (!isDragging) return;
    const x = e.touches[0].pageX - containerRef.current.offsetLeft;
    const walk = (x - startX) * 2;
    containerRef.current.scrollLeft = scrollLeft - walk;
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    updateActiveIndex();
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
    updateActiveIndex();
  };

  const updateActiveIndex = () => {
    if (containerRef.current) {
      const cardWidth = containerRef.current.children[0].offsetWidth; // full card width, no gap
      const newIndex = Math.round(containerRef.current.scrollLeft / cardWidth);
      setActiveIndex(
        Math.max(0, Math.min(newIndex, targetAudience.length - 1))
      );
    }
  };

  const scrollLeftBtn = () => {
    const newIndex = Math.max(0, activeIndex - 1);
    scrollToIndex(newIndex);
  };

  const scrollRightBtn = () => {
    const newIndex = Math.min(targetAudience.length - 1, activeIndex + 1);
    scrollToIndex(newIndex);
  };

  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      container.addEventListener("scroll", updateActiveIndex);
      return () => container.removeEventListener("scroll", updateActiveIndex);
    }
  }, []);

  return (
    <div className="bg-black py-20 lg:py-16 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Section Header */}
        <div className="flex items-center justify-center mb-12">
          <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-blue-500/10 border border-blue-500/30 backdrop-blur-sm">
            <span className="text-sm font-bold text-blue-400 tracking-wide uppercase flex items-center gap-2">
              <span className="text-lg">🎯</span> Who is this for?
            </span>
          </div>
        </div>

        <div className="space-y-10">
          {/* Desktop Grid */}
          <div className="hidden md:grid md:grid-cols-2 gap-6">
            {targetAudience.map((item, index) => (
              <div
                key={index}
                className={`group relative bg-black border ${item.borderColor} rounded-xl p-6
                 ${item.hoverBorderColor} transition-all duration-300
                 hover:bg-gradient-to-br ${item.hoverGradient}
                 transform hover:-translate-y-1`}
              >
                <div
                  className={`absolute -inset-0.5 bg-gradient-to-r ${item.gradient}
                   rounded-xl blur opacity-10 group-hover:opacity-30
                   transition duration-300`}
                />

                <div className="relative">
                  {/* Header */}
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-3xl">{item.emoji}</span>
                    <div className="px-2 py-1 bg-gray-800/50 rounded text-xs font-mono text-gray-400 border border-gray-700">
                      {item.codeHint}
                    </div>
                  </div>

                  {/* Content */}
                  <div className="space-y-2">
                    <h3 className="text-lg font-bold text-white group-hover:text-blue-300 transition-colors">
                      {item.title}
                    </h3>
                    <p className="text-gray-400 group-hover:text-gray-200 transition-colors text-sm">
                      {item.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Mobile Swipeable Carousel */}
          <div className="md:hidden relative">
            <div
              ref={containerRef}
              className="flex overflow-x-auto pb-4 snap-x snap-mandatory scrollbar-hide cursor-grab active:cursor-grabbing"
              style={{
                scrollbarWidth: "none",
                msOverflowStyle: "none",
                WebkitOverflowScrolling: "touch",
              }}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              {targetAudience.map((item, index) => (
                <div
                  key={index}
                  className={`flex-shrink-0 w-full bg-black border ${item.borderColor} rounded-xl p-6
                   transition-all duration-300 bg-gradient-to-br ${item.gradient} snap-center
                   hover:border-opacity-70 select-none`}
                  style={{ userSelect: "none" }}
                >
                  <div className="flex flex-col items-center text-center gap-4">
                    <div className="flex items-center gap-3">
                      <span className="text-3xl">{item.emoji}</span>
                      <div className="px-3 py-1 bg-gray-800/50 rounded text-xs font-mono text-gray-400 border border-gray-700">
                        {item.codeHint}
                      </div>
                    </div>
                    <div className="space-y-2">
                      <h3 className="text-lg font-bold text-white">
                        {item.title}
                      </h3>
                      <p className="text-gray-300 text-sm">
                        {item.description}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Mobile Navigation Arrows */}
          <div className="md:hidden flex justify-center mt-6 gap-6">
            <button
              onClick={scrollLeftBtn}
              disabled={activeIndex === 0}
              className={`p-3 rounded-full border transition-all duration-300 ${
                activeIndex === 0
                  ? "bg-gray-800/50 border-gray-700 text-gray-600 cursor-not-allowed"
                  : "bg-blue-500/10 border-blue-500/30 text-blue-400 hover:bg-blue-500/20 hover:border-blue-400"
              }`}
              aria-label="Previous slide"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            <button
              onClick={scrollRightBtn}
              disabled={activeIndex === targetAudience.length - 1}
              className={`p-3 rounded-full border transition-all duration-300 ${
                activeIndex === targetAudience.length - 1
                  ? "bg-gray-800/50 border-gray-700 text-gray-600 cursor-not-allowed"
                  : "bg-blue-500/10 border-blue-500/30 text-blue-400 hover:bg-blue-500/20 hover:border-blue-400"
              }`}
              aria-label="Next slide"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
          </div>

          {/* Mobile Navigation Dots */}
          {/* <div className="flex justify-center mt-4 gap-2">
            {targetAudience.map((_, index) => (
              <button
                key={index}
                onClick={() => scrollToIndex(index)}
                className={`h-2.5 rounded-full transition-all duration-300 ${
                  activeIndex === index
                    ? "bg-blue-400 w-8"
                    : "bg-gray-600 w-2.5 hover:bg-gray-500"
                }`}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div> */}

          {/* Swipe hint */}
          {/* <div className="text-center mt-4">
            <p className="text-gray-500 text-xs">👆 Swipe or drag to explore</p>
          </div> */}
        </div>

        {/* Call-out Section */}
        {/* Call-out Section */}
        {/* Call-out Section */}
        {/* Call-out Section */}
        <div className="group relative overflow-hidden rounded-2xl p-8 transition-all duration-500 hover:scale-[1.02] mt-8 md:mt-16">
          {/* Animated gradient background */}
          <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-purple-900/50 to-slate-900 opacity-90"></div>
          <div className="absolute inset-0 bg-gradient-to-r from-blue-600/10 via-purple-600/20 to-pink-600/10 animate-pulse"></div>

          {/* Subtle pattern overlay */}
          <div
            className="absolute inset-0 opacity-5"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='1'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            }}
          ></div>

          {/* Mobile Layout */}
          <div className="relative flex flex-col items-center text-center gap-6 md:hidden">
            {/* Icon with glow effect */}
            <div className="relative">
              <div className="absolute inset-0 bg-red-500/30 rounded-full blur-xl scale-150 animate-pulse"></div>
              <div className="relative bg-gradient-to-br from-red-500/20 to-orange-500/20 p-4 rounded-full border border-red-400/30">
                <div className="text-4xl">😤</div>
              </div>
            </div>

            {/* Content */}
            <div className="space-y-4">
              <div className="inline-block px-4 py-2 bg-red-500/10 border border-red-400/30 rounded-full text-red-300 text-sm font-medium tracking-wide">
                STUCK IN THE LOOP?
              </div>
              <h3 className="text-2xl font-bold text-white leading-tight">
                If your strategy is just
                <br />
                <span className="text-transparent bg-gradient-to-r from-red-400 via-orange-400 to-yellow-400 bg-clip-text font-black text-3xl">
                  "Apply. Pray. Repeat."
                </span>
              </h3>
              <div className="h-1 w-20 bg-gradient-to-r from-blue-400 to-purple-400 rounded-full mx-auto"></div>
              <p className="text-xl font-bold text-transparent bg-gradient-to-r from-green-400 to-blue-400 bg-clip-text">
                This changes everything.
              </p>
            </div>
          </div>

          {/* Desktop Layout */}
          {/* Desktop Layout */}
          {/* Desktop Layout */}
          <div className="hidden md:flex md:flex-row md:items-center md:gap-8 ">
            {/* Icon with glow effect - positioned on the left for desktop */}
            <div className="relative flex-shrink-0">
              <div className="absolute inset-0 bg-red-500/30 rounded-full blur-xl scale-150 animate-pulse"></div>
              <div className="relative bg-gradient-to-br from-red-500/20 to-orange-500/20 p-4 rounded-full border border-red-400/30">
                <div className="text-4xl">😤</div>
              </div>
            </div>

            {/* Content - centered and taking full width */}
            <div className="flex-1 flex flex-col items-center text-center space-y-4">
              <div className="inline-block px-4 py-2 bg-red-500/15 border border-red-400/40 rounded-full text-red-200 text-sm font-semibold tracking-wide mr-20">
                STUCK IN THE LOOP?
              </div>
              <h3 className="text-2xl font-bold text-white leading-tight">
                If your strategy is just{" "}
                <span className="text-transparent bg-gradient-to-r from-red-400 via-orange-400 to-yellow-400 bg-clip-text font-black text-3xl drop-shadow-sm">
                  "Apply. Pray. Repeat."
                </span>
              </h3>
              <div className="h-1 w-20 bg-gradient-to-r from-blue-400 to-purple-400 rounded-full  md:item-center md:mr-16"></div>
              <p className="text-xl font-bold text-white md:mr-16">
                This changes everything.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WhoIsThisFor;
